import { useEffect, useRef } from 'react';
import './background.css';
import '../styles/brand-art.css';
import { mountArt } from './brandArt';

/*
  The still background behind every signed-in page. Theme system, phase 3,
  14 September 2026; docs/THEME-SYSTEM.md sections 4 and 5.

  It replaced the moving Phase 3 "atmosphere" (src/phase3/Atmosphere.jsx, now
  gone): five drifting layers per theme, a veil, and a push between screens.

  **Drawn once**, by the shared frame, rather than by each screen: the screens
  used to build their own frames, and a background added in a dozen places
  drifts in a dozen ways.

  All of its look is CSS reading the theme's tokens. The one thing done here is
  telling the browser early which single picture to fetch: a preload link for
  the active theme and mode's artwork, replaced when the theme or the device's
  light or dark setting changes, and removed when there is none. Nothing else
  is ever fetched, so nobody downloads the set (section 5).
*/
export default function ThemeBackground() {
  usePreloadActiveArt();
  const host = useBrandArt();
  return (
    <div className="app-background-layer" aria-hidden="true">
      <div className="app-background" />
      <div className="oh-art" ref={host} />
    </div>
  );
}

/*
  **The theme's artwork, rebrand step 6, 17 September 2026.** It is drawn rather
  than fetched: `src/theme/brandArt.js` builds the ten themes out of HTML, CSS
  and SVG, ported from the approved reference. This hook mounts it, rebuilds it
  when the theme or light-and-dark changes, and takes it down on the way out.

  It reads the theme from <html>, which `src/theme/appearance.js` sets, so the
  art and the colours can never disagree about which theme is on.
*/
function useBrandArt() {
  const host = useRef(null);
  const nowShowing = useRef('');
  useEffect(() => {
    const el = host.current;
    if (!el) return undefined;
    let stop = null;
    const sync = () => {
      const d = document.documentElement.dataset;
      const key = (d.theme || '') + ':' + (d.colorMode || '');
      if (key === nowShowing.current) return;
      nowShowing.current = key;
      if (stop) { stop(); stop = null; }
      if (!d.theme) return;
      stop = mountArt(el, { theme: d.theme, dark: d.colorMode === 'dark' });
    };
    sync();
    const watcher = new MutationObserver(sync);
    watcher.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme', 'data-color-mode'] });
    return () => {
      watcher.disconnect();
      if (stop) stop();
      nowShowing.current = '';
    };
  }, []);
  return host;
}

const LINK_ID = 'oh-theme-art-preload';

function artUrl() {
  const value = getComputedStyle(document.documentElement).getPropertyValue('--art-image').trim();
  const m = value.match(/^url\(\s*["']?([^"')]+)["']?\s*\)$/);
  return m ? m[1] : null;
}

function usePreloadActiveArt() {
  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const sync = () => {
      const url = artUrl();
      let link = document.getElementById(LINK_ID);
      if (!url) { if (link) link.remove(); return; }
      if (link && link.getAttribute('href') === url) return;
      if (!link) {
        link = document.createElement('link');
        link.id = LINK_ID;
        link.rel = 'preload';
        link.as = 'image';
        document.head.appendChild(link);
      }
      link.setAttribute('href', url);
    };
    sync();
    // The theme and mode are attributes on <html>; the device's own light or
    // dark setting matters once the mode is automatic (theme phase 4).
    const watcher = new MutationObserver(sync);
    watcher.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme', 'data-mode'] });
    const scheme = window.matchMedia ? window.matchMedia('(prefers-color-scheme: dark)') : null;
    if (scheme && scheme.addEventListener) scheme.addEventListener('change', sync);
    return () => {
      watcher.disconnect();
      if (scheme && scheme.removeEventListener) scheme.removeEventListener('change', sync);
    };
  }, []);
}
